import Chip from 'components/@extended/Chip';
import { Block as BlockIcon, LockIcon, LockOutlined as LockOutlinedIcon } from 'components/icons';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Tooltip } from 'components/ui/tooltip';
import Blocked from 'pages/matters/Blocked';
import { ReactElement } from 'react';
import { MatterVisibility, MatterVisibilityInfo } from 'types/matters';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';

/**
 * `LIMITED` is the only reduced scope the reads emit; anything else means the viewer can open the case.
 *
 * The parameter is deliberately the visibility scope alone rather than {@link MatterVisibilityInfo}: the
 * codebase spells case access three ways — `visibility` here, `isRestricted` on `MatterDto` and
 * `isReadable` on `MatterExperienceRecordDto` — and only this one is read here. Accepting the wider shape
 * made a `MatterDto` compile (the two share `isConfidential`, which defeats TypeScript's weak-type check)
 * and silently answer `true` for a restricted case. Narrowed to a single optional member, a shape without
 * `visibility` shares nothing with it and is rejected instead; those two spellings are passed by hand.
 */
export const hasMatterAccess = (matter: Pick<MatterVisibilityInfo, 'visibility'> | undefined | null) =>
    matter?.visibility !== MatterVisibility.LIMITED;

/**
 * How the icons sit in their surroundings: `chip` is the 24px grey pill used in chip rows (case
 * list, case header), `option` is the same pill inside a picker (its options and selected value),
 * `inline` is the bare glyph used inside a row's text or link line. `pill` is the new design's chip
 * row: confidential becomes the labelled {@link ConfidentialChip}, the other two keep their chip.
 * In the new design every variant but `option` shows confidential as that chip: a padlock alone is
 * for pickers only.
 */
type Variant = 'inline' | 'chip' | 'option' | 'pill';

/**
 * The new design's confidential marker, the same red for every record that can be confidential:
 * a padlock, then what is confidential ("Confidential case", "Confidential debriefing", …).
 */
export const ConfidentialChip = ({ label, className }: { label: string; className?: string }) => (
    <span
        className={cn(
            'bg-danger-tint text-danger-ink text-caption inline-flex h-5 items-center gap-1 rounded-lg px-1.5 font-semibold whitespace-nowrap',
            className
        )}
    >
        <LockIcon className="size-3" />
        {label}
    </span>
);

/**
 * For indicators leading a row's text in a list cell: in the new design, inside a row given `new:flex-wrap`,
 * they move to a line of their own under the text, where a list cell keeps its chips.
 */
export const BOTTOM_LINE_IN_NEW_DESIGN = 'new:order-last new:basis-full';

interface Props {
    /** The case is marked confidential. Shown regardless of access — it says nothing about access. */
    isConfidential?: boolean | null;
    /**
     * The viewer can open the case. Surfaces whose payload carries no visibility scope leave this
     * true: they only ever list cases the viewer can reach.
     */
    hasAccess?: boolean;
    /**
     * The case is blocked. Rendered only when the viewer has access — blocking concerns time entry,
     * which is impossible on a case that cannot be opened, so grey and red never appear together.
     */
    isBlocked?: boolean;
    /** Names what the block stops; the short form suits a list row, the long one a page header. */
    blockedTooltipKey?: string;
    variant?: Variant;
    className?: string;
}

/** Inline glyphs carry their own muted colour; inside a chip the pill's own rules set it. */
const glyphClass = (variant: Variant) => cn('h-4 w-4', variant === 'inline' && 'text-fg-secondary');

/** One indicator: the glyph alone inline, or wrapped in the 24px grey pill for a chip row. */
const Indicator = ({ label, glyph, variant }: { label: string; glyph: ReactElement; variant: Variant }) => (
    <Tooltip content={label}>
        {variant !== 'inline' ? (
            <span className="inline-flex">
                <Chip size="small" type="partyType" label={glyph} />
            </span>
        ) : (
            glyph
        )}
    </Tooltip>
);

/**
 * The three unrelated facts a case can carry, in their fixed order: confidential (padlock), no
 * access (grey slashed circle), blocked (red slashed circle). Each is independent of the others and
 * none of them changes who may open what — this renders, it does not gate.
 *
 * Renders nothing when the case carries none of them, so it is safe to drop in unconditionally.
 */
const MatterIndicators = ({
    isConfidential,
    hasAccess = true,
    isBlocked,
    blockedTooltipKey = 'dashboard.tooltips.timecard-blocked-short',
    variant = 'inline',
    className,
}: Props) => {
    const isNewDesign = useIsNewDesign();
    const showConfidential = isConfidential === true;
    const showNoAccess = !hasAccess;
    const showBlocked = hasAccess && isBlocked === true;
    const confidentialAsChip = variant === 'pill' || (isNewDesign && variant !== 'option');

    if (!showConfidential && !showNoAccess && !showBlocked) {
        return null;
    }

    const confidentialLabel = t('matters.confidential');
    const noAccessLabel = t('matters.restricted-access');

    return (
        <span className={cn('inline-flex shrink-0 items-center gap-1', className)}>
            {showConfidential && confidentialAsChip && <ConfidentialChip label={confidentialLabel} />}
            {showConfidential && !confidentialAsChip && (
                <Indicator
                    label={confidentialLabel}
                    variant={variant}
                    glyph={
                        <LockOutlinedIcon
                            role="img"
                            aria-label={confidentialLabel}
                            // Glyphs default to aria-hidden unless given a `titleAccess`, which would also
                            // render a native <title> tooltip on top of this one.
                            aria-hidden={false}
                            className={glyphClass(variant)}
                        />
                    }
                />
            )}
            {showNoAccess && (
                <Indicator
                    label={noAccessLabel}
                    variant={variant}
                    glyph={
                        <BlockIcon
                            role="img"
                            aria-label={noAccessLabel}
                            aria-hidden={false}
                            className={glyphClass(variant)}
                        />
                    }
                />
            )}
            {/* The red badge keeps its own chrome — a rounded square, not the grey pill. */}
            {showBlocked && (
                <Blocked
                    tooltipText={blockedTooltipKey}
                    className={variant === 'inline' ? 'h-4 w-4 p-[3px]' : undefined}
                />
            )}
        </span>
    );
};

export default MatterIndicators;
